import { useLazyQuery } from '@apollo/client';
import { useContext, useEffect, useState } from 'react';
import { Context } from '../../../../../context/AppContext';
import { MAX_SUGGESTIONS } from '../../../constants';
import debounce from '../../utils/debounce';
import { getNestedValue, isNotEmptyArray } from '../../utils/helpers';
import SearchAreaSuggestQuery from './SearchAreaSuggestQuery';

/**
 * Transforms fetched data object into an array of search area suggestions
 * @param {Object} data The fetched data
 * @returns {Array} Array of search area suggestions
 */
const transformData = data => {
  const edges = getNestedValue(data, 'searchAreaSuggest', 'edges');
  if (!isNotEmptyArray(edges)) return [];
  return edges.map(
    ({ node: { description, id, name, path, statisticalAreas } }) => ({
      id,
      name,
      path,
      statisticalAreaIds: statisticalAreas.map(sA => sA.id),
      suggestLabel: description,
    })
  );
};

/**
 * Logic for fetching and returning search area suggestions
 * @returns {Object} {
 * error,
 * fetchSearchAreaSuggestions,
 * loading,
 * searchAreaSuggestions,
 * setSearchAreaSuggestions,
 * topSearchAreaSuggestion,
 * }
 */
const useSearchAreaSuggest = () => {
  const [searchAreaSuggestions, setSearchAreaSuggestions] = useState([]);
  const [topSearchAreaSuggestion, setTopSearchAreaSuggestion] = useState();
  const [fetch, { data, error, loading }] = useLazyQuery(
    SearchAreaSuggestQuery
  );
  const {
    media: { mobile },
  } = useContext(Context);

  const maxSuggestions = mobile
    ? MAX_SUGGESTIONS.MOBILE.SEARCH_AREAS
    : MAX_SUGGESTIONS.DESKTOP.SEARCH_AREAS;

  /**
   * Fetch the search area suggestions from the server
   * @param {string} args.query The search term to query
   * @param {bool} args.debounceFetch Should the fetch be debounced? Defaults to true
   * @returns {void}
   */
  const fetchSearchAreaSuggestions = ({ query, debounceFetch = true }) => {
    const fetchParams = {
      variables: {
        query,
        first: maxSuggestions,
        enabled: true,
      },
    };
    if (debounceFetch) debounce(() => fetch(fetchParams));
    else fetch(fetchParams);
  };

  /**
   * Update the search area suggestions whenever the fetched data is changed
   */
  useEffect(() => {
    if (!loading) {
      const hasSuggestions = isNotEmptyArray(
        getNestedValue(data, 'searchAreaSuggest', 'edges')
      );
      if (hasSuggestions) {
        setSearchAreaSuggestions(transformData(data));
      } else {
        setSearchAreaSuggestions();
      }
    }
  }, [data, loading]);

  /**
   * Update the top search area suggestion whenever the search area suggestions are changed
   */
  useEffect(() => {
    if (isNotEmptyArray(searchAreaSuggestions)) {
      setTopSearchAreaSuggestion(searchAreaSuggestions[0]);
    }
  }, [searchAreaSuggestions]);

  return {
    error,
    fetchSearchAreaSuggestions,
    loading,
    searchAreaSuggestions,
    setSearchAreaSuggestions,
    topSearchAreaSuggestion,
  };
};

export default useSearchAreaSuggest;
